import { useRef, useState } from 'react'
import { importerArchiveProcore, type BilanImport, type EtapeImport } from '../lib/importProcore'

/**
 * Import d'une archive Procore (fichier ZIP de l'outil « Archive du projet »)
 * dans ce chantier. Réservé aux administrateurs, sur ordinateur : l'archive
 * fait souvent plusieurs centaines de mégaoctets.
 */
export function ImportProcore({ chantierId }: { chantierId: string }) {
  const [etape, setEtape] = useState<EtapeImport | null>(null)
  const [bilan, setBilan] = useState<BilanImport | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const arret = useRef(false)

  async function lancer(fichier: File | undefined) {
    if (!fichier) return
    if (
      !window.confirm(
        `Importer « ${fichier.name} » dans ce chantier ?\n\n` +
          "Garde cette fenêtre ouverte jusqu'à la fin : l'envoi peut prendre plusieurs minutes. " +
          "Si l'import est interrompu, il suffit de le relancer avec le même fichier : " +
          'ce qui est déjà passé sera sauté.'
      )
    ) {
      return
    }
    arret.current = false
    setEnCours(true)
    setErreur(null)
    setBilan(null)
    try {
      setBilan(await importerArchiveProcore(fichier, chantierId, setEtape, () => arret.current))
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
      setEtape(null)
    }
  }

  return (
    <>
      <p className="meta" style={{ marginTop: 12 }}>
        Importer une archive Procore (ZIP) : plans, photos, rapports journaliers, déficiences et QRT.
      </p>
      {!enCours && (
        <label className="bouton creux" style={{ marginTop: 8, cursor: 'pointer' }}>
          Importer une archive Procore
          <input
            type="file"
            accept=".zip,application/zip"
            hidden
            onChange={(e) => {
              void lancer(e.target.files?.[0])
              e.target.value = ''
            }}
          />
        </label>
      )}
      {enCours && (
        <div className="avis" style={{ marginTop: 8 }}>
          <p>
            {etape ? `${etape.etape} : ${etape.fait} / ${etape.total}` : 'Préparation…'}
          </p>
          <p className="meta">Garde cette fenêtre ouverte.</p>
          <button className="bouton creux" style={{ marginTop: 8 }} onClick={() => (arret.current = true)}>
            Arrêter
          </button>
        </div>
      )}
      {erreur && <p className="avis erreur" style={{ marginTop: 8 }}>{erreur}</p>}
      {bilan && (
        <div className="avis" style={{ marginTop: 8 }}>
          <p style={{ fontWeight: 700 }}>Import terminé.</p>
          <p className="meta">
            {bilan.plans} feuilles de plans · {bilan.anciennesRevisions} anciennes révisions ·{' '}
            {bilan.photos} nouvelles photos · {bilan.pagesArchives} pages d'archives
          </p>
          {bilan.erreurs.length > 0 && (
            <>
              <p style={{ marginTop: 8, fontWeight: 700 }}>
                {bilan.erreurs.length} élément(s) non importé(s) — relance l'import pour réessayer :
              </p>
              <ul className="meta">
                {bilan.erreurs.slice(0, 20).map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </>
          )}
          {bilan.ignores.length > 0 && (
            <p className="meta" style={{ marginTop: 8 }}>
              Fichiers d'un autre type, non importés : {bilan.ignores.slice(0, 10).join(', ')}
              {bilan.ignores.length > 10 ? '…' : ''}
            </p>
          )}
        </div>
      )}
    </>
  )
}
